import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { txnLabel, formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";

export default function UserHistory() {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    const q = type === "all" ? "" : `?type=${type}`;
    api.get(`/user/transactions${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const filtered = (rows || []).filter((t) => {
    if (!from && !to) return true;
    const d = new Date(t.created_at);
    if (from && d < new Date(from + "T00:00:00")) return false;
    if (to && d > new Date(to + "T23:59:59")) return false;
    return true;
  });

  return (
    <div>
      <PageHeader title="Transaction History" desc="Every deposit, withdrawal and adjustment with UTR, IST time and remarks." />

      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <Tabs value={type} onValueChange={setType}><TabsList className="bg-slate-900/60">{["all", "deposit", "withdrawal", "adjustment", "dispute_hold", "dispute_release", "reversal"].map((s) => <TabsTrigger key={s} value={s} data-testid={`hist-type-${s}`} className="capitalize">{txnLabel(s)}</TabsTrigger>)}</TabsList></Tabs>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs text-slate-400">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="hist-from" className="mt-1 bg-slate-900/60 border-slate-700 h-10" /></div>
          <div><Label className="text-xs text-slate-400">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="hist-to" className="mt-1 bg-slate-900/60 border-slate-700 h-10" /></div>
        </div>
      </div>

      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-400">Txn ID</TableHead><TableHead className="text-slate-400">Type</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead><TableHead className="text-slate-400">UTR</TableHead>
                <TableHead className="text-slate-400">Date (IST)</TableHead><TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Remark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!rows ? <TableRow><TableCell colSpan={7} className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400 mx-auto" /></TableCell></TableRow> :
                filtered.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">No transactions match.</TableCell></TableRow> :
                filtered.map((t) => (
                  <TableRow key={t.id} className="border-white/5" data-testid={`hist-row-${t.txn_id}`}>
                    <TableCell className="font-mono text-xs text-slate-400">{t.txn_id}</TableCell>
                    <TableCell className="capitalize text-sm">{txnLabel(t.type)}{t.direction ? <span className="text-slate-500"> ({t.direction})</span> : ""}</TableCell>
                    <TableCell className={`font-mono ${t.type === "withdrawal" || t.direction === "debit" ? "text-rose-300" : "text-emerald-300"}`}>{formatINR(t.amount)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.utr || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{formatIST(t.created_at)}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell className="text-xs text-slate-400 max-w-[160px] truncate" title={t.remark}>{t.remark || "—"}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
